import { useCallback } from 'react';
import { toast } from 'sonner';

import { CashFlowType, DataType, FrequencyType, WithdrawalStatus } from '@ndvr/portfolio-api';

import { RequestReason } from '@/cip/_openapi/portfolio-optimizer';
import { formatCurrency, formatDate, nextWorkingDay } from 'utils';
import { useCreateCashFlowConfig, useSavePlannedWithdrawal } from 'views/portfolio/cash-flow-queries';
import { Toast } from 'widgets';

import { useCreateInvestmentRequest } from './investment-request-queries';

/**
 * - `success`: every requested step completed.
 * - `partial`: the investment request was created, but a downstream step (cash-flow
 *   config and/or marking the planned withdrawal COMPLETED) failed. The IR exists
 *   and the user has been told what they need to do manually.
 * - `failure`: the investment request itself was not created. Nothing happened.
 */
export type SubmitInvestmentRequestOutcome = 'success' | 'partial' | 'failure';

export interface SubmitInvestmentRequestInput {
    accountGUID: string;
    accountGroupGUID: string;
    householdGUID: string;
    reason: RequestReason;
    description?: string;
    /** Required when `reason === RequestReason.WITHDRAWAL`. */
    withdrawalAmount?: number;
    /** When true and reason is WITHDRAWAL, the planned withdrawal is immediately marked COMPLETED. */
    alreadyWired?: boolean;
}

/**
 * Orchestrates creation of an investment request and, for urgent withdrawals,
 * the follow-up cash-flow config and optional "already wired" status update.
 *
 * Toasts are emitted internally so all callers report the same messages.
 *
 * Returns a {@link SubmitInvestmentRequestOutcome}. Callers decide how to react —
 * the typical mapping is to keep the form open on `failure` and close on the
 * other two so the user isn't stuck staring at a stale form.
 */
export const useSubmitInvestmentRequest = () => {
    const { mutateAsync: createInvestmentRequest, isPending: isCreatingInvestmentRequest } =
        useCreateInvestmentRequest();
    const { mutateAsync: createCashFlowConfig, isPending: isCreatingWithdrawal } = useCreateCashFlowConfig();
    const { mutateAsync: savePlannedWithdrawal, isPending: isSavingWithdrawalStatus } = useSavePlannedWithdrawal();
    const isPending = isCreatingInvestmentRequest || isCreatingWithdrawal || isSavingWithdrawalStatus;

    const submit = useCallback(
        async (input: SubmitInvestmentRequestInput): Promise<SubmitInvestmentRequestOutcome> => {
            const isWithdrawal = input.reason === RequestReason.WITHDRAWAL;
            const alreadyWired = isWithdrawal && !!input.alreadyWired;
            const amount = input.withdrawalAmount;
            const withdrawalDate = formatDate(nextWorkingDay(), 'SERVER');
            const description = isWithdrawal
                ? `Urgent withdrawal - ${formatCurrency(amount)}${input.description ? ` ${input.description}` : ''}`
                : (input.description ?? '');

            // Step 1: create the investment request. If this fails, abort and let the caller stay open.
            try {
                await createInvestmentRequest({
                    investmentRequestCreatePayload: {
                        portfolioGuid: input.accountGUID,
                        reason: input.reason,
                        description,
                    },
                });
            } catch (error) {
                Toast.forError(error, 'Error creating investment request');
                return 'failure';
            }

            if (!isWithdrawal) {
                toast.success('Investment request created');
                return 'success';
            }

            // Step 2: create the one-time withdrawal cash-flow config.
            // From here on, partial-failure toasts still close the caller because the IR is already created.
            let cashFlowConfigGUID: string | null = null;
            try {
                [cashFlowConfigGUID] = await createCashFlowConfig({
                    accountGroupGUID: input.accountGroupGUID,
                    cashFlowConfig: {
                        accountGUID: input.accountGUID,
                        amount: String(amount),
                        cashFlowType: CashFlowType.WITHDRAWAL,
                        inflationConfig: { inflationAdjusted: false },
                        factorDateFrame: {
                            startDate: withdrawalDate,
                            frequencyType: FrequencyType.ONE_TIME,
                        },
                        title: {
                            encryptionContextGUID: input.householdGUID,
                            plainText: 'Ad Hoc Withdrawal',
                            dataType: DataType.SI_FACTOR_TITLE,
                        },
                    },
                });
            } catch (error) {
                Toast.forError(
                    error,
                    'Investment request created but failed to create withdrawal. Please create the withdrawal manually.',
                );
                return 'partial';
            }

            // Step 3: if the funds were already wired out-of-band, immediately mark the planned withdrawal COMPLETED.
            if (alreadyWired && cashFlowConfigGUID) {
                try {
                    await savePlannedWithdrawal({
                        cashFlowConfigGUID,
                        withdrawalDate,
                        withdrawalStatus: WithdrawalStatus.COMPLETED,
                    });
                    toast.success('Withdrawal created and marked completed');
                    return 'success';
                } catch (error) {
                    Toast.forError(
                        error,
                        'Withdrawal created but failed to mark it as completed. Please update its status manually.',
                    );
                    return 'partial';
                }
            }

            toast.success('Withdrawal created');
            return 'success';
        },
        [createInvestmentRequest, createCashFlowConfig, savePlannedWithdrawal],
    );

    return { submit, isPending };
};
